import React, { useCallback, useMemo } from 'react';
import {
  useNotify,
  useRecordContext,
  useUpdate,
  useRefresh,
  SelectField,
  Form,
  required,
} from 'react-admin';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Typography,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';

import { capitalize } from 'lodash';
import useDialogStatus from '../hooks/useDialogStatus';

import casePriority, { CasePriority } from '../constants/casePriority';
import RadioInput from './RadioInput';

type CaseDoc = {
  id: string;
  priority: CasePriority;
};

const UpdateCasePriorityField = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext<CaseDoc>();
  const { id, priority } = record || {};

  const { open, openDialog, closeDialog } = useDialogStatus();

  const [closed] = useUpdate();

  const handleSubmit = useCallback(({ priority: newPriority }: { priority?: string }) => {
    closed(
      'cases',
      { id: `${id}/priority`, data: { priority: newPriority } },
      {
        onSuccess: () => {
          notify('Case priority have been updated');
          closeDialog();
          refresh();
        },
        onError: () => {
          notify('Could not update case priority', { type: 'error' });
        },
      },
    );
  }, [closeDialog, closed, id, notify, refresh]);

  const {
    allPriorityChoices,
    updatePriorityChoices,
  } = useMemo(() => {
    const allChoices = Object.values(casePriority).map((item) => ({
      name: capitalize(item),
      id: item,
    }));

    const updateChoices = allChoices.filter((choice) => choice.id !== priority);

    return {
      allPriorityChoices: allChoices,
      updatePriorityChoices: updateChoices,
    };
  }, [priority]);

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="xs">
        <DialogTitle fontWeight="bold">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">Update priority</Typography>
            <IconButton onClick={closeDialog}>
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <Form
          record={{ priority: priority ?? casePriority.NORMAL }}
          onSubmit={handleSubmit}
        >
          <DialogContent sx={{
            display: 'flex',
            justifyContent: 'start',
            paddingX: '18%',
          }}
          >
            <Box display="flex" gap={5} flexDirection="column">
              <Box display="flex" flexDirection="column">
                <FormLabel focused={false}>Current priority</FormLabel>
                <SelectField
                  source="priority"
                  choices={allPriorityChoices}
                />
              </Box>
              <FormControl>
                <FormLabel focused={false}>Change to:</FormLabel>
                <RadioInput
                  source="priority"
                  validate={required()}
                  choices={updatePriorityChoices}
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="contained" type="submit">Save</Button>
          </DialogActions>
        </Form>
      </Dialog>
      <Button
        variant="outlined"
        onClick={openDialog}
      >
        Update priority
      </Button>
    </>
  );
};

export default UpdateCasePriorityField;
